import React from 'react'
import { Helmet } from 'react-helmet-async'

const TermsPage: React.FC = () => {
  const lastUpdated = "December 27, 2024"

  return (
    <>
      <Helmet>
        <title>Terms & Conditions | FGA Prestige - Legal Terms</title>
        <meta name="description" content="Review the terms and conditions for FGA Prestige luxury fashion services and membership agreements." />
        <meta name="keywords" content="FGA Prestige terms, conditions, legal agreement, membership terms, luxury fashion services" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      
      <main className="min-h-screen py-20 bg-white">
        <div className="fga-container">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="text-center mb-12">
              <h1 className="fga-title text-3xl md:text-4xl mb-6" style={{ color: 'var(--fga-title-gold-dark)' }}>
                Terms & Conditions
              </h1>
              <p className="fga-paragraph text-lg">
                FGA Prestige Service Agreement and Terms of Use
              </p>
              <div className="text-sm mt-4" style={{ color: 'var(--fga-paragraph-grey)' }}>
                Last Updated: {lastUpdated}
              </div>
            </div>

            {/* Content */}
            <div className="bg-white shadow-lg p-8 md:p-12 space-y-8">
              
              {/* Introduction */}
              <section>
                <div className="fga-paragraph space-y-4">
                  <p>
                    These Terms and Conditions ("Terms") govern all services delivered by FGA Prestige ("we," "our," or "us"). By requesting or using our services, you agree to be bound by these Terms.
                  </p>
                </div>
              </section>

              {/* 1. Definitions and Interpretation */}
              <section>
                <h2 className="fga-title text-2xl mb-4" style={{ color: 'var(--fga-title-gold-dark)' }}>
                  1. Definitions and Interpretation
                </h2>
                <div className="fga-paragraph space-y-4">
                  <div className="space-y-3">
                    <p><strong>Benefits:</strong> Privileges and offers made available by third-party partners ("Suppliers") to FGA Prestige members.</p>
                    <p><strong>Member:</strong> An approved individual holding a current FGA Prestige Membership.</p>
                    <p><strong>Membership:</strong> The exclusive program granting access to FGA Prestige concierge and lifestyle services.</p>
                    <p><strong>Membership Fee:</strong> Annual dues payable in advance and notified to you at application and each renewal.</p>
                    <p><strong>Joining Fee:</strong> One-time fee charged upon joining.</p>
                    <p><strong>Membership Card:</strong> The digital or physical ID issued to Members after acceptance.</p>
                    <p><strong>Member Portal:</strong> A dedicated digital platform for Members, accessible via FGA Prestige's app or website where available.</p>
                    <p><strong>Supplier:</strong> Any third-party engaged to provide goods or services upon Member request.</p>
                    <p><strong>Request:</strong> A service or purchase instruction placed by a Member to FGA Prestige.</p>
                    <p><strong>Services:</strong> All concierge and lifestyle management services provided by FGA Prestige.</p>
                    <p><strong>Website:</strong> The official FGA Prestige website.</p>
                  </div>
                  
                  <p className="mt-6">
                    <strong>Interpretation:</strong> These Terms include individuals and corporate entities; all references apply equally to successors and assigns. Any phrase including "including" is illustrative, not limiting; references to "writing" also cover email.
                  </p>
                </div>
              </section>

              {/* 2. Membership Application */}
              <section>
                <h2 className="fga-title text-2xl mb-4" style={{ color: 'var(--fga-title-gold-dark)' }}>
                  2. Membership Application
                </h2>
                <div className="fga-paragraph space-y-4">
                  <p>
                    All Memberships are subject to approval at FGA Prestige's discretion, and acceptance will be confirmed in writing. The reasons for any rejection may remain confidential.
                  </p>
                  <p>
                    Applicants must provide accurate and up-to-date personal details. Failure to do so may invalidate Membership and any transactions.
                  </p>
                  <p>
                    Membership is strictly personal; usage by others is not allowed except with explicit written authorisation (such as a designated assistant).
                  </p>
                  <p>
                    Members are responsible for the security and confidentiality of their login credentials for the Member Portal.
                  </p>
                  <p>
                    Lost or stolen Membership Cards must be reported promptly.
                  </p>
                </div>
              </section>

              {/* 3. Membership Fees, Upgrades, Cancellations, Renewals */}
              <section>
                <h2 className="fga-title text-2xl mb-4" style={{ color: 'var(--fga-title-gold-dark)' }}>
                  3. Membership Fees, Upgrades, Cancellations, Renewals
                </h2>
                <div className="fga-paragraph space-y-4">
                  <p>
                    Initial and renewal Membership and Joining Fees will be confirmed to you before processing.
                  </p>
                  <p>
                    Membership Fees are non-refundable except as outlined in these Terms. The Joining Fee is strictly non-refundable.
                  </p>
                  <p>
                    Members may cancel within 14 days of acceptance for a full refund of the Membership Fee, less the value of any services already provided.
                  </p>
                  <p>
                    To cancel or avoid renewal, written notice must be given at least 30 days prior to the annual renewal date.
                  </p>
                  <p>
                    FGA Prestige reserves the right to suspend or terminate Membership in cases of breach, with refunds prorated as appropriate.
                  </p>
                  <p>
                    Payment may be made by card, bank transfer, or approved direct debit methods.
                  </p>
                  <p>
                    Upgrades to higher Membership tiers or plan changes may be subject to additional fees calculated pro-rata.
                  </p>
                </div>
              </section>

              {/* 4. Services and Requests */}
              <section>
                <h2 className="fga-title text-2xl mb-4" style={{ color: 'var(--fga-title-gold-dark)' }}>
                  4. Services and Requests
                </h2>
                <div className="fga-paragraph space-y-4">
                  <p>
                    Services are delivered according to Member Requests, subject to availability, legality, and suitability within relevant jurisdictions.
                  </p>
                  <p>
                    FGA Prestige liaises with Suppliers on your behalf but is not responsible for the fulfilment, quality, or reliability of third-party goods or services.
                  </p>
                  <p>
                    Any service delivery dates provided are indicative; time is not of the essence.
                  </p>
                  <p>
                    FGA Prestige may accept commissions upon arranging Supplier products or services.
                  </p>
                  <p>
                    Telephone queries and service calls may be recorded for training and quality assurance.
                  </p>
                </div>
              </section>

              {/* 5. Service Requests Placement */}
              <section>
                <h2 className="fga-title text-2xl mb-4" style={{ color: 'var(--fga-title-gold-dark)' }}>
                  5. Service Requests Placement
                </h2>
                <div className="fga-paragraph space-y-4">
                  <p>
                    Requests may be submitted via phone, authorised messaging apps, email, or via the Member Portal.
                  </p>
                  <p>
                    Members should contact their home office for all Requests, including those outside the home territory.
                  </p>
                  <p>
                    FGA Prestige may refuse or withdraw services or requests at its discretion.
                  </p>
                  <p>
                    Members must ensure all purchase instructions and payment details are correct and have sufficient funds.
                  </p>
                  <p>
                    Additional FGA Prestige service fees may be charged and will be notified in advance.
                  </p>
                </div>
              </section>

              {/* 6. Cancellations, Refunds, Returns */}
              <section>
                <h2 className="fga-title text-2xl mb-4" style={{ color: 'var(--fga-title-gold-dark)' }}>
                  6. Cancellations, Refunds, Returns
                </h2>
                <div className="fga-paragraph space-y-4">
                  <p>
                    Contracts for third-party goods or services are exclusively between the Member and the Supplier, not FGA Prestige, and are governed by Supplier terms.
                  </p>
                  <p>
                    FGA Prestige is not liable for product or service descriptions or delivery, nor for customs or international shipping issues.
                  </p>
                  <p>
                    Returns, exchanges, or cancellations are subject to the Supplier's policies, which will be advised in advance where possible.
                  </p>
                </div>
              </section>

              {/* 7. Suppliers and Third Parties */}
              <section>
                <h2 className="fga-title text-2xl mb-4" style={{ color: 'var(--fga-title-gold-dark)' }}>
                  7. Suppliers and Third Parties
                </h2>
                <div className="fga-paragraph space-y-4">
                  <p>
                    All third-party benefits and offers are subject to availability and may change without notice.
                  </p>
                  <p>
                    Supplier terms and conditions apply at all times to goods and services procured.
                  </p>
                  <p>
                    FGA Prestige is not liable for the actions, errors, or omissions of Suppliers or for any losses resulting from their goods or services.
                  </p>
                </div>
              </section>

              {/* 8. Limitation of Liability */}
              <section>
                <h2 className="fga-title text-2xl mb-4" style={{ color: 'var(--fga-title-gold-dark)' }}>
                  8. Limitation of Liability
                </h2>
                <div className="fga-paragraph space-y-4">
                  <p>
                    FGA Prestige's liability is limited to the value of the annual Membership Fee paid.
                  </p>
                  <p>
                    FGA Prestige is not responsible for indirect loss or consequential damages, and all third-party contracts are at the Member's own risk.
                  </p>
                  <p>
                    Any exclusion or limitation of liability does not apply to personal injury, death, or fraud.
                  </p>
                </div>
              </section>

              {/* 9. Commencement and Termination */}
              <section>
                <h2 className="fga-title text-2xl mb-4" style={{ color: 'var(--fga-title-gold-dark)' }}>
                  9. Commencement and Termination
                </h2>
                <div className="fga-paragraph space-y-4">
                  <p>
                    These Terms are binding upon written acceptance of Membership by FGA Prestige and remain in effect for the term of Membership.
                  </p>
                  <p>
                    Termination or expiry of Membership ends service obligations.
                  </p>
                </div>
              </section>

              {/* 10. General */}
              <section>
                <h2 className="fga-title text-2xl mb-4" style={{ color: 'var(--fga-title-gold-dark)' }}>
                  10. General
                </h2>
                <div className="fga-paragraph space-y-4">
                  <p>
                    Data protection and privacy are maintained as per the prevailing FGA Prestige Privacy Policy.
                  </p>
                  <p>
                    FGA Prestige reserves the right to assign, delegate, or subcontract any obligations to third parties.
                  </p>
                  <p>
                    No provision of these Terms is intended to create a partnership, joint venture, or agency.
                  </p>
                  <p>
                    These Terms constitute the entire agreement, superseding previous oral or written agreements.
                  </p>
                  <p>
                    English law governs these Terms and any dispute will be subject to the exclusive jurisdiction of the courts where FGA Prestige is headquartered.
                  </p>
                </div>
              </section>

              {/* Acceptance */}
              <section className="bg-yellow-50 p-6 border-l-4 border-yellow-500">
                <h2 className="fga-title text-xl mb-4" style={{ color: 'var(--fga-title-gold-dark)' }}>
                  Acceptance of Terms
                </h2>
                <p className="fga-paragraph">
                  By using FGA Prestige services, you acknowledge that you have read, understood, and 
                  agree to be bound by these Terms and Conditions. These Terms constitute the entire 
                  agreement between you and FGA Prestige regarding your use of our services.
                </p>
              </section>
            </div>
          </div>
        </div>
      </main>
    </>
  )
}

export default TermsPage